
import React, { useState } from 'react';
import { LucideIcon, Plus } from 'lucide-react';
import SectionCard from './SectionCard';
import { Topic } from '../../types/topic';

interface SectionMetadata {
  title: string;
  icon?: LucideIcon;
  description?: string;
  showDivider?: boolean;
}

interface PageSectionProps {
  sectionKey: string;
  sectionMetadata: SectionMetadata;
  topics: Topic[];
  cardVariant?: 'default' | 'compact' | 'detailed';
  gridCols?: 1 | 2 | 3 | 4;
  maxRows?: number;
  showProgress?: boolean;
  showStats?: boolean;
  hideHeader?: boolean;
  progressMap?: Record<string, number>;
  className?: string;
}

/**
 * Generic reusable section component for displaying groups of topic cards
 * Handles section header, divider, and grid layout
 */
const PageSection: React.FC<PageSectionProps> = ({
  sectionKey,
  sectionMetadata,
  topics,
  cardVariant = 'default',
  gridCols = 3,
  maxRows = 2,
  showProgress = false,
  showStats = false,
  hideHeader = false,
  progressMap = {},
  className = '',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Don't render if no topics (PageSection will handle empty state if needed)
  // Note: Empty sections can still be useful for placeholders
  if (!topics || topics.length === 0) {
    return null;
  }

  // Sort topics by sectionOrder if available, otherwise by order
  const sortedTopics = [...topics].sort((a, b) => {
    if (a.sectionOrder !== undefined && b.sectionOrder !== undefined) {
      return a.sectionOrder - b.sectionOrder;
    }
    return (a.order || 0) - (b.order || 0);
  });

  // Calculate max topics based on gridCols and maxRows
  // For responsive: mobile (1 col), tablet (2 cols), desktop (gridCols)
  // We'll use desktop viewport calculation: gridCols × maxRows
  const maxTopicsToShow = gridCols * maxRows;

  // Determine which topics to show
  const topicsToShow = isExpanded
    ? sortedTopics
    : sortedTopics.slice(0, maxTopicsToShow);

  const remainingCount = sortedTopics.length - maxTopicsToShow;
  const hasMoreTopics = remainingCount > 0 && !isExpanded;

  // Grid column classes
  const gridColsClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
  }[gridCols];

  const Icon = sectionMetadata.icon;

  return (
    <div id={`section-${sectionKey}`} className={`mb-16 ${className}`}>
      {/* Section Header */}
      {!hideHeader && <div className="flex items-center mb-8">
        {Icon && (
          <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-sky-500 rounded-lg flex items-center justify-center mr-3">
            <Icon className="w-6 h-6 text-white" />
          </div>
        )}
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {sectionMetadata.title}
          </h2>
          {sectionMetadata.description && (
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {sectionMetadata.description}
            </p>
          )}
        </div>
        {sectionMetadata.showDivider && (
          <div className="flex-1 h-px bg-gradient-to-r from-gray-300 via-gray-200 to-transparent dark:from-gray-700 dark:via-gray-600 ml-4"></div>
        )}
      </div>}

      {/* Topics Grid */}
      <div className={`grid ${gridColsClass} gap-6`}>
        {topicsToShow.map((topic) => (
          <SectionCard
            key={topic.id}
            topic={topic}
            variant={cardVariant}
            showProgress={showProgress}
            showStats={showStats}
            progressPercent={progressMap[topic.slug] || progressMap[topic.id] || 0}
          />
        ))}

        {/* Show More Indicator */}
        {hasMoreTopics && (
          <button
            onClick={() => setIsExpanded(true)}
            className="section-show-more group col-span-full"
          >
            <div className="section-show-more__icon">
              <Plus />
            </div>
            <span className="section-show-more__copy">
              <strong>Show More</strong>
              <span>{remainingCount} more {remainingCount === 1 ? 'topic' : 'topics'} available</span>
            </span>
          </button>
        )}
      </div>

      {/* Show Less Button (when expanded) */}
      {isExpanded && sortedTopics.length > maxTopicsToShow && (
        <div className="mt-6 text-center">
          <button
            onClick={() => {
              setIsExpanded(false);
              // Scroll to section header smoothly
              const sectionElement = document.getElementById(`section-${sectionKey}`);
              if (sectionElement) {
                sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
            className="section-show-less px-6 py-2 text-sm font-medium rounded-lg transition-colors"
          >
            Show Less
          </button>
        </div>
      )}
    </div>
  );
};

export default PageSection;
